/**
 * A linked project's keys (spec UI §4, row click): each key as on the Edit screen, its accounts,
 * Connected or Disconnected, and its doc status. Admins get Refresh docs and Remove key per key;
 * members see the same drawer with no actions.
 */
import { useMutation } from '@tanstack/react-query';
import { Pencil, RefreshCw, Trash2, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { ActionTooltip } from '@/_shared/components/ActionTooltip';
import { Badge } from '@/_shared/components/ui/badge';
import { Button } from '@/_shared/components/ui/button';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/_shared/components/ui/sheet';
import { useWorkspace } from '@/_shared/contexts/WorkspaceContext';
import { useOrpc } from '@/_shared/hooks/useOrpc';

import { isDocBusy, type LinkedProject, type LinkedProjectKey, projectPathLabel } from '../../lib/linkedRepoList';
import { AccountKeyLabel, RawAccountKey } from './AccountKeyPicker';
import { rpcErrorMessage } from './linkedRepoSchema';
import { useInvalidateLinkedRepos } from './useInvalidateLinkedRepos';

export const ADD_ACCOUNT_FIRST = 'Add an account for this key first';
const DOC_BUSY = 'Docs are being generated. Try again when they finish.';
/**
 * A Connected key whose doc was deleted (from Context Hub): Refresh has nothing to rebuild. Removing the
 * key and adding it back recreates the doc; a project's only key is removed by Unlink, so it says that.
 */
function noDocToRefresh(keyCount: number): string {
  return keyCount > 1
    ? 'No docs to refresh. Remove this key, then add it back with Edit, to build them again.'
    : 'No docs to refresh. Unlink the repository and link it again to build them again.';
}

function docStatusText(key: LinkedProjectKey): string {
  switch (key.docState?.status) {
    case 'ready':
      return key.accountStatus === 'disconnected' ? 'Built; can’t rebuild until an account reads this key' : 'Ready';
    case 'generating':
      return 'Generating docs…';
    case 'failed':
      return 'Generation failed';
    case undefined:
    default:
      return 'No docs yet';
  }
}

function KeyCard({
  project,
  keyRow,
  isAdmin,
  onRemoveKey,
}: {
  project: LinkedProject;
  keyRow: LinkedProjectKey;
  isAdmin: boolean;
  onRemoveKey: (project: LinkedProject, key: LinkedProjectKey) => void;
}): React.JSX.Element {
  const orpc = useOrpc();
  const { wp } = useWorkspace();
  const invalidate = useInvalidateLinkedRepos();
  const refresh = useMutation(
    orpc.warehouseProjects.regenerate.mutationOptions({
      onSuccess: () => {
        invalidate();
        toast.success(`Refreshing docs for ${keyRow.display.database}.`);
      },
      onError: (err) => toast.error(rpcErrorMessage(err, 'Could not refresh the docs.')),
    }),
  );
  const disconnected = keyRow.accountStatus === 'disconnected';
  const busy = isDocBusy(keyRow.docState);
  const refreshBlocked = disconnected
    ? ADD_ACCOUNT_FIRST
    : keyRow.docState === null
      ? noDocToRefresh(project.keys.length)
      : busy
        ? DOC_BUSY
        : null;

  return (
    <li className="flex min-w-0 flex-col gap-3 rounded-xl border bg-card p-4">
      <div className="flex min-w-0 items-start justify-between gap-3">
        <AccountKeyLabel display={keyRow.display} className="flex-1" />
        <span className="flex shrink-0 items-center gap-2">
          <RawAccountKey accountKey={keyRow.accountKey} database={keyRow.display.database} />
          {disconnected ? <Badge variant="warning">Disconnected</Badge> : <Badge variant="success">Connected</Badge>}
        </span>
      </div>
      <dl className="grid grid-cols-[5rem_minmax(0,1fr)] gap-x-3 gap-y-1.5 text-sm">
        <dt className="text-muted-foreground">Accounts</dt>
        <dd className="min-w-0 break-words text-foreground">
          {keyRow.accounts.length > 0
            ? keyRow.accounts.map((a) => a.name).join(', ')
            : 'None. The account that read this database was removed.'}
        </dd>
        <dt className="text-muted-foreground">Docs</dt>
        <dd className="min-w-0 text-foreground">
          {docStatusText(keyRow)}
          {keyRow.docState && (
            <>
              {' · '}
              <Link to={wp(`/context/generated/${keyRow.docState.id}`)} className="text-brand hover:underline">
                View in Context Hub
              </Link>
            </>
          )}
        </dd>
      </dl>
      {disconnected && (
        <p className="text-xs text-muted-foreground">
          No warehouse account reads {keyRow.display.database}, so there is nothing to build its docs from. Add an
          account for that database, or remove the key.
        </p>
      )}
      {isAdmin && (
        <div className="flex flex-wrap gap-2">
          <ActionTooltip show={refreshBlocked !== null} message={refreshBlocked ?? ''}>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              Icon={<RefreshCw />}
              loading={refresh.isPending}
              disabled={refreshBlocked !== null}
              onClick={() => refresh.mutate({ accountKey: keyRow.accountKey })}
              label="Refresh docs"
              aria-label={`Refresh docs for ${keyRow.display.database}`}
            />
          </ActionTooltip>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            Icon={<Trash2 />}
            onClick={() => onRemoveKey(project, keyRow)}
            label="Remove key"
            aria-label={`Remove ${keyRow.display.database}`}
          />
        </div>
      )}
    </li>
  );
}

export function ProjectDrawer({
  project,
  onOpenChange,
  isAdmin,
  onEdit,
  onRemoveKey,
}: {
  project: LinkedProject | null;
  onOpenChange: (open: boolean) => void;
  isAdmin: boolean;
  onEdit: (project: LinkedProject) => void;
  onRemoveKey: (project: LinkedProject, key: LinkedProjectKey) => void;
}): React.JSX.Element {
  return (
    <Sheet open={project !== null} onOpenChange={(open) => !open && onOpenChange(false)}>
      <SheetContent side="right" showCloseButton={false} className="w-full gap-0 sm:max-w-xl">
        {project && (
          <>
            <SheetHeader className="border-b border-border p-4">
              <div className="flex items-start justify-between gap-2">
                <span className="flex min-w-0 flex-col">
                  <SheetTitle className="truncate text-sm font-medium text-foreground" title={project.repoFullName}>
                    {project.repoFullName}
                  </SheetTitle>
                  <SheetDescription className="truncate text-xs text-muted-foreground">
                    {projectPathLabel(project.projectPath)} · {project.projectTypeLabel} ·{' '}
                    {project.keys.length === 1 ? '1 account key' : `${project.keys.length} account keys`}
                  </SheetDescription>
                </span>
                <span className="flex shrink-0 items-center gap-1">
                  {isAdmin && (
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      Icon={<Pencil />}
                      onClick={() => onEdit(project)}
                      label="Edit"
                    />
                  )}
                  <SheetClose asChild>
                    <Button type="button" variant="tertiary" iconOnly aria-label="Close" Icon={<X />} />
                  </SheetClose>
                </span>
              </div>
            </SheetHeader>
            <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-4">
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-xs font-bold tracking-wider text-muted-foreground uppercase">Account keys</h3>
                <span className="text-xs text-muted-foreground">{project.statusSummary}</span>
              </div>
              <ul className="flex flex-col gap-3">
                {project.keys.map((key) => (
                  <KeyCard
                    key={key.accountKey}
                    project={project}
                    keyRow={key}
                    isAdmin={isAdmin}
                    onRemoveKey={onRemoveKey}
                  />
                ))}
              </ul>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
